import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import ViewDrawer from './Drawers/View';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { useSettings } from '../../../../context/settings';
import AgGridTable from '../../../../components/Tables/AgGrid';
import { printSlug } from '../../../../utils/helpers/text';
import { TfiFilter } from 'react-icons/tfi';
import { Badge } from 'reactstrap';
import FiltersDrawer from './Drawers/Filters';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/sales/payments/edit/${data.id}`, { state: { paymentData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setPaymentToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function PaymentsPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['min_amount', 'max_amount', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getPayments = useCallback(async () => {
    setLoading(true);
    let query = '/sales/payments?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.payments?.data) {
      setRowData(data.payments.data);
      setTotalPages(data.payments.last_page);
      setTotalRecords(data.payments.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  useEffect(() => {
    getPayments();
  }, [getPayments]);

  const deletePayment = async () => {
    const data = await del('/sales/payments/delete/' + paymentToDelete);

    if (data?.success) {
      toast.success(data.message || t('sales.paymentDeleted'));
      getPayments();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setPaymentToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'payment_date', headerName: t('common.date'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    { field: 'reference', headerName: t('common.reference'), flex: 1, minWidth: 150, sortable: true },
    {
      field: 'customer.company_name',
      headerName: t('sales.client'),
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: (params) => {
        let companyName = params?.data?.customer?.company_name || '';
        if(companyName) {
          return companyName;
        }
        return params?.data?.customer?.firstname + ' ' + params?.data?.customer?.lastname || '';
      }
    },
    {
      field: 'payment_method', 
      headerName: t('sales.paymentMode'), 
      width: 120, 
      sortable: true,
      valueFormatter: (params) => {
        return printSlug(params?.data?.payment_method) || '';
      }
    },
    {
      field: 'amount_received',
      headerName: t('common.amount'),
      width: 120,
      sortable: true,
      valueFormatter: (params) => {
        return params?.data?.amount_received + ' ' + params?.data?.currency;
      }
    },
    {
      headerName: i18n.t('common.options'),
      width: 110,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setPaymentToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    setSelectedPaymentId(event.data.id);
    setDrawerOpen(true);
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sidebar.paymentsReceived')} onReload={getPayments} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/sales/payments/create')}>
            {t('sales.newPayment')}
          </DefaultButton>
        </PageSubHeader>
        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={paymentToDelete !== null}
        title={t('sales.deletePayment')}
        message={t('sales.deletePaymentConfirm')}
        onConfirm={deletePayment}
        toggle={() => setPaymentToDelete(null)}
      />

      <ViewDrawer
        paymentId={selectedPaymentId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDelete={() => {
          setDrawerOpen(false);
          setPaymentToDelete(selectedPaymentId);
        }}
        refreshInvoices={getPayments}
      />
    </SidebarLayout>
  );
}

export default PaymentsPage;
